// usePayouts — TanStack Query hook for one filtered page of the current
// merchant's payouts, with the server's totals for the whole filter.
//
// queryKey starts ["payouts", merchantId] so every existing
// invalidateQueries({ queryKey: ["payouts", merchantId] }) still refreshes
// it; the params ride after so each filter/page is its own cache entry.
//
// Polls every 30s (ROADMAP criterion 4, D-03) so pending → success/failure
// shows without a reload. placeholderData keeps the previous page on screen
// while the next one loads, instead of flashing the skeleton on every
// filter keystroke or page turn.
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { listPayouts, type PayoutListParams } from "@/api/payouts";
import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";

export function usePayouts(params: PayoutListParams = {}) {
  const status = useAuth((s) => s.status);
  const currentMerchantId = useUI((s) => s.currentMerchantId);

  return useQuery({
    queryKey: [
      "payouts",
      currentMerchantId,
      { ...params, min_amount_minor: params.min_amount_minor?.toString() },
    ],
    queryFn: () => listPayouts(currentMerchantId!, params),
    enabled: status === "authenticated" && currentMerchantId != null,
    staleTime: 10_000,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}
